import { useGlobalMutation } from 'state/query-client/hooks';
import { syncIntegration } from 'services/integration-service';
import { useConfig } from './use-config';

/**
 * Custom hook to sync integrations
 *
 * This hook provides a mutation function to sync integrations with external services.
 * It uses the global mutation pattern for consistent error handling and authentication management.
 *
 * Features:
 * - Automatic configuration injection
 * - Global error handling with authentication management
 * - Loading state management
 * - TypeScript support for request parameters
 *
 * @returns {Object} The mutation object with methods and state
 * @returns {Function} mutate - Function to trigger the integration sync
 * @returns {Function} mutateAsync - Async function to trigger the integration sync
 * @returns {boolean} isPending - Whether the mutation is in progress
 * @returns {boolean} isSuccess - Whether the mutation completed successfully
 * @returns {boolean} isError - Whether the mutation failed
 * @returns {Error | null} error - The error if mutation failed
 *
 * @example
 * const { mutate: syncIntegration, isPending } = useSyncIntegration();
 * 
 * const handleSync = (connectionId) => {
 *   syncIntegration(connectionId, {
 *     onSuccess: (data) => {
 *       console.log('Integration synced successfully:', data);
 *     },
 *     onError: (error) => {
 *       console.error('Failed to sync integration:', error);
 *     }
 *   });
 * };
 */
export const useSyncIntegration = () => {
    const { configurationSettings } = useConfig();

    return useGlobalMutation<{ success: boolean; message: string }, Error, string>({
        mutationFn: async (connectionId: string) => {
            if (!configurationSettings) {
                throw new Error('Configuration settings are not available');
            }
            return await syncIntegration(configurationSettings, connectionId);
        },
    });
};
